import { useEffect, useRef } from 'react';

const tiers = [
  {
    name: 'Diamante',
    quota: '1 cota',
    highlight: true,
    badge: 'Exclusivo',
    perks: [
      'Naming rights do evento',
      'Logo em toda comunicação',
      'Ativação exclusiva no evento',
      '5 convites VIP',
      'Vídeo institucional na abertura',
      'Entrevista exclusiva com Dafne Borsatti',
      'Relatório completo de métricas',
    ],
    price: 'Sob consulta',
  },
  {
    name: 'Ouro',
    quota: '3 cotas',
    highlight: false,
    badge: '',
    perks: [
      'Logo em destaque',
      '3 convites VIP',
      'Ativação compartilhada',
      'Menção em mídia',
      'Material no kit das participantes',
      'Foto oficial com palestrantes',
    ],
    price: 'Sob consulta',
  },
  {
    name: 'Prata',
    quota: '5 cotas',
    highlight: false,
    badge: '',
    perks: [
      'Logo digital',
      '2 convites',
      'Material no kit',
      '3 posts nas redes sociais',
      'Acesso ao mailing pós-evento',
    ],
    price: 'Sob consulta',
  },
  {
    name: 'Apoio',
    quota: 'Cotas disponíveis',
    highlight: false,
    badge: '',
    perks: [
      'Logo no site do evento',
      '1 convite',
      '1 menção nas redes sociais',
    ],
    price: 'Sob consulta',
  },
];

interface SponsorshipSectionProps {
  onSponsor: () => void;
}

export default function SponsorshipSection({ onSponsor }: SponsorshipSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-target').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="patrocinio"
      ref={sectionRef}
      className="bg-cream py-28 md:py-36 px-6 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="animate-target opacity-0 transition-all duration-700 mb-4" style={{ transform: 'translateY(30px)' }}>
            <span className="font-sans text-xs tracking-widest3 uppercase text-gold" style={{ letterSpacing: '0.3em' }}>
              Patrocínio
            </span>
          </div>
          <div className="animate-target opacity-0 transition-all duration-700 mb-5" style={{ transform: 'translateY(30px)' }}>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-obsidian leading-tight">
              Sua marca ao lado das{' '}
              <span className="italic text-gold">Leoas.</span>
            </h2>
          </div>
          <div className="animate-target opacity-0 transition-all duration-700 w-16 h-px bg-gold mx-auto mb-6" style={{ transform: 'translateY(30px)' }} />
          <p className="animate-target opacity-0 transition-all duration-700 font-sans text-sm md:text-base text-obsidian/60 max-w-xl mx-auto font-light" style={{ transform: 'translateY(30px)' }}>
            Posicione sua marca diante de mulheres que decidem, contratam e influenciam.
          </p>
        </div>

        {/* Tiers grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {tiers.map((tier, index) => (
            <div
              key={tier.name}
              className={`animate-target opacity-0 transition-all duration-700 flex flex-col relative overflow-hidden ${
                tier.highlight
                  ? 'bg-obsidian border border-gold text-cream'
                  : 'bg-white border border-obsidian/10 text-obsidian hover:border-gold/40 hover:shadow-none'
              }`}
              style={{ transform: 'translateY(30px)', transitionDelay: `${index * 100}ms` }}
            >
              {tier.badge && (
                <div className="absolute top-0 right-0 bg-gold text-obsidian font-sans text-xs font-semibold px-3 py-1 uppercase" style={{ letterSpacing: '0.15em' }}>
                  {tier.badge}
                </div>
              )}

              <div className="p-7 flex-1">
                <p className="font-sans text-xs tracking-widest uppercase text-gold mb-1" style={{ letterSpacing: '0.2em' }}>
                  {tier.quota}
                </p>
                <h3 className={`font-serif text-2xl font-bold mb-1 ${tier.highlight ? 'text-gold' : 'text-obsidian'}`}>
                  {tier.name}
                </h3>
                <div className="w-8 h-px bg-gold mb-6" />

                <ul className="space-y-3">
                  {tier.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-3">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-gold text-sm" />
                      </div>
                      <span className={`font-sans text-xs leading-relaxed font-light ${tier.highlight ? 'text-cream/75' : 'text-obsidian/65'}`}>
                        {perk}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={`px-7 pb-7 mt-auto border-t ${tier.highlight ? 'border-gold/20' : 'border-obsidian/10'} pt-6`}>
                <p className={`font-sans text-xs tracking-widest uppercase mb-4 ${tier.highlight ? 'text-gold/60' : 'text-obsidian/40'}`} style={{ letterSpacing: '0.2em' }}>
                  {tier.price}
                </p>
                <a
                  href="https://wa.me/5566996418971?text=Ol%C3%A1,%20eu%20gostaria%20de%20saber%20mai%20sobre%20o%20evento:%20%22O%20Rugido%20das%20Leoas%22.%20Pode%20me%20ajudar?"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full font-sans text-xs tracking-widest uppercase py-3 transition-all duration-300 cursor-pointer whitespace-nowrap text-center block ${
                    tier.highlight
                      ? 'bg-gold text-obsidian hover:bg-gold-dark font-semibold'
                      : 'border border-obsidian/30 text-obsidian hover:border-gold hover:text-gold'
                  }`}
                  style={{ letterSpacing: '0.15em' }}
                >
                  Quero Patrocinar
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="animate-target opacity-0 transition-all duration-700 text-center" style={{ transform: 'translateY(30px)' }}>
          <p className="font-sans text-sm text-obsidian/50 font-light">
            Cotas limitadas. Posições estratégicas reservadas por ordem de contato.
          </p>
        </div>
      </div>
    </section>
  );
}
